import { z } from 'zod';
import { completeReportingRange } from './reporting.js';
export const growthRate = z.object({
    valuePct: z.number().nullable(),
    numerator: z.number().int().nonnegative(),
    denominator: z.number().int().nonnegative(),
    status: z.enum(['measured', 'insufficient_data']),
});
export const growthHistoryUnavailableReason = z.enum([
    'tracking_not_started',
    'tracking_not_mature',
    'tracking_coverage_incomplete',
    'range_precedes_tracking',
    'truth_refresh_incomplete',
]);
export const growthForecastUnavailableReason = z.enum([
    'tracking_not_started',
    'tracking_not_mature',
    'tracking_coverage_incomplete',
    'range_precedes_tracking',
    'membership_sync_unhealthy',
    'truth_refresh_incomplete',
]);
export const growthDataWarning = z.enum([
    'truth_not_scanned',
    'truth_scan_stale',
    'truth_refresh_incomplete',
    'short_booking_window',
    'show_up_low_sample',
    'closing_low_sample',
    'tracking_not_started',
    'tracking_not_mature',
    'tracking_coverage_incomplete',
    'range_precedes_tracking',
    'membership_sync_missing',
    'membership_sync_stale',
    'membership_sync_failed',
    'membership_sync_partial',
    'booking_backfill_partial',
]);
export const growthSummaryResponse = z.object({
    ok: z.literal(true),
    asOf: z.string(),
    reportingTimezone: z.literal('Europe/Oslo'),
    range: completeReportingRange,
    membership: z.object({
        activeNow: z.number().int().nonnegative(),
        frozenNow: z.number().int().nonnegative(),
        excludedNow: z.number().int().nonnegative(),
        rawActiveNow: z.number().int().nonnegative(),
        rawFrozenNow: z.number().int().nonnegative(),
        excludedActiveNow: z.number().int().nonnegative(),
        excludedFrozenNow: z.number().int().nonnegative(),
        breakEvenMembers: z.number().int().positive(),
        gap: z.number().int().nonnegative(),
        progressPct: z.number().nonnegative(),
        status: z.enum(['reached', 'gap']),
    }),
    bookingPace: z.object({
        bookings: z.number().int().nonnegative(),
        evidenceDays: z.number().int().positive(),
        actualPerDay: z.number().nonnegative(),
        actualPerWeek: z.number().nonnegative(),
        targetPerDay: z.number().positive(),
        targetPerWeek: z.number().positive(),
        status: z.enum(['on_track', 'behind', 'insufficient_data']),
        source: z.literal('growth_booking_facts'),
        dataStatus: z.enum(['measured', 'partial_backfill', 'stale_truth']),
        reasonCode: z
            .enum([
            'booking_backfill_partial',
            'truth_not_scanned',
            'truth_scan_stale',
            'truth_refresh_incomplete',
        ])
            .nullable(),
        trackingStartedDay: z.string().nullable(),
        firstCompleteTrackedDay: z.string().nullable(),
    }),
    funnel: z.object({
        bookings: z.number().int().nonnegative(),
        showUp: growthRate,
        closing: growthRate,
        cancellation: growthRate,
    }),
    membershipFlow: z.object({
        measurementMode: z.literal('daily_status_checkpoints'),
        status: z.enum(['measured', 'unavailable']),
        reasonCode: growthHistoryUnavailableReason.nullable(),
        trackingStartedDay: z.string().nullable(),
        firstCompleteTrackedDay: z.string().nullable(),
        trackedCompleteDays: z.number().int().nonnegative(),
        requiredCompleteDays: z.literal(28),
        checkpointCount: z.number().int().nonnegative(),
        startActive: z.number().int().nonnegative().nullable(),
        endActive: z.number().int().nonnegative().nullable(),
        joined: z.number().int().nonnegative().nullable(),
        returned: z.number().int().nonnegative().nullable(),
        lost: z.number().int().nonnegative().nullable(),
        frozen: z.number().int().nonnegative().nullable(),
        unfrozen: z.number().int().nonnegative().nullable(),
        breakdownStatus: z.literal('unavailable'),
        breakdownReasonCode: z.literal('momence_transition_timestamps_unavailable'),
        net: z.number().int().nullable(),
    }),
    forecast: z.object({
        status: z.enum(['reached', 'projected', 'not_reachable', 'unavailable']),
        reasonCode: growthForecastUnavailableReason.nullable(),
        evidenceDays: z.number().int().nonnegative(),
        netMembersPerWeek: z.number().nullable(),
        weeksToBreakEven: z.number().nonnegative().nullable(),
        projectedBreakEvenDate: z.string().nullable(),
    }),
    dataQuality: z.object({
        status: z.enum(['ready', 'warning']),
        truthScannedAt: z.string().nullable(),
        truthScan: z.object({
            scannedAt: z.string().nullable(),
            ageMinutes: z.number().int().nonnegative().nullable(),
            healthy: z.boolean(),
            latestRunStatus: z.string().nullable(),
            latestRunFinishedAt: z.string().nullable(),
        }),
        membershipStatusSync: z.object({
            finishedAt: z.string().nullable(),
            status: z.string().nullable(),
            itemsProcessed: z.number().int().nonnegative(),
            itemsFailed: z.number().int().nonnegative(),
            ageMinutes: z.number().int().nonnegative().nullable(),
            healthy: z.boolean(),
        }),
        sourceTables: z.array(z.string()).min(1),
        warnings: z.array(growthDataWarning),
    }),
});
//# sourceMappingURL=growth.js.map